import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';

interface UnsavedChangesRegistryContextValue {
    setDirtyState: (sourceId: string, isDirty: boolean) => void;
}

const UnsavedChangesRegistryContext =
    createContext<UnsavedChangesRegistryContextValue | null>(null);

type DirtySourceMap = Record<string, true>;

function buildNextDirtySourceMap(
    currentValue: DirtySourceMap,
    sourceId: string,
    isDirty: boolean
): DirtySourceMap {
    if (isDirty) {
        if (currentValue[sourceId] === true) {
            return currentValue;
        }
        return {
            ...currentValue,
            [sourceId]: true,
        };
    }
    if (currentValue[sourceId] !== true) {
        return currentValue;
    }
    const nextValue: DirtySourceMap = { ...currentValue };
    delete nextValue[sourceId];
    return nextValue;
}

export function UnsavedChangesGuardProvider(props: {
    children: ReactNode;
}): ReactElement {
    const [dirtySourceMap, setDirtySourceMap] = useState<DirtySourceMap>({});
    const hasUnsavedChanges: boolean = useMemo(
        () => Object.keys(dirtySourceMap).length > 0,
        [dirtySourceMap]
    );
    const blocker = useBlocker(hasUnsavedChanges);

    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (!hasUnsavedChanges) {
                    return;
                }
                event.preventDefault();
                event.returnValue = '';
            },
            [hasUnsavedChanges]
        )
    );

    useEffect(() => {
        if (!hasUnsavedChanges && blocker.state === 'blocked') {
            blocker.proceed();
        }
    }, [blocker, hasUnsavedChanges]);

    const contextValue: UnsavedChangesRegistryContextValue = useMemo(
        () => ({
            setDirtyState: (sourceId: string, isDirty: boolean) => {
                setDirtySourceMap((currentValue) =>
                    buildNextDirtySourceMap(currentValue, sourceId, isDirty)
                );
            },
        }),
        []
    );

    return (
        <UnsavedChangesRegistryContext.Provider value={contextValue}>
            {props.children}
            <ConfirmationModal
                open={blocker.state === 'blocked'}
                onOpenChange={(open) => {
                    if (!open && blocker.state === 'blocked') {
                        blocker.reset();
                    }
                }}
                title="Unsaved changes"
                message="Navigating away will cause all unsaved edits on this page to be lost."
                confirmLabel="Discard changes"
                cancelLabel="Stay on page"
                onConfirm={() => {
                    if (blocker.state === 'blocked') {
                        blocker.proceed();
                    }
                }}
                onCancel={() => {
                    if (blocker.state === 'blocked') {
                        blocker.reset();
                    }
                }}
            />
        </UnsavedChangesRegistryContext.Provider>
    );
}

export function useUnsavedChangesRegistration(isDirty: boolean): void {
    const registryContext = useContext(UnsavedChangesRegistryContext);
    const sourceId = useId();

    useEffect(() => {
        if (registryContext === null) {
            return;
        }
        registryContext.setDirtyState(sourceId, isDirty);
        return () => {
            registryContext.setDirtyState(sourceId, false);
        };
    }, [isDirty, registryContext, sourceId]);
}
